import React from 'react'
import PropTypes from 'prop-types'
import { toUrlSafe } from 'utils/stringHelpers'
import ClassCard from 'components/dashboard/ClassCard'
import { TilesContainer } from 'components/dashboard/ActiveTabProgramCards/component.styles'
import CreateCourse from 'experienceComponents/CreateCourse'
import { k8V2ContentfulMappings } from 'utils/product/contentfulMappings'
import CreateSmallGroup from 'experienceComponents/CreateSmallGroup'
import TierLabel from 'components/TierLabel'
import {
  getClassCompleteLessons,
  getClassTotalLessons,
} from 'components/ProductLessons/classListHelpers'
import {
  ClassesContainer,
  ButtonsContainer,
  Title,
  TitleContainer,
  StyledButton,
  StyledCreateClassIcon,
  StyledAwardIcon,
  HiddenCreateCourseContainer,
  TilesContainerWrapper,
} from './component.styles'
import {
  TITLE,
  TIER_2_TITLE,
  CREATE_CLASS_BUTTON_TEXT,
  TRAINING_BUTTON_TEXT,
  TILES_CONTAINER_CLASS_NAME,
} from './constants'

const K8DigitalProgramCardV2 = ({
  classes,
  classMenuButtonRefs,
  contentfulCourses,
  courseManager,
  createCourseRef,
  createSmallGroupRef,
  currentProgramName,
  handleCreateClassClick,
  handleCreateSmallGroupClick,
  internalTitle,
  isK5Product,
  isMsProduct,
  lessonCompletionGoalValue,
  onClassBlur,
  onClassFocus,
  onComponentClick,
  onTier2ResourcesClick,
  onTrainingClick,
  setFocus,
  tier2ResourcesLabel,
}) => {
  const tier1Classes = classes?.filter(c => c.tier === 1)
  const tier2Classes = classes?.filter(c => c.tier === 2)
  const program = isMsProduct
    ? 'middle-school'
    : isK5Product
    ? 'elementary'
    : currentProgramName
  return (
    <TilesContainerWrapper>
      {/* FirstTimeClasses component - always render when showFirstTimeClasses is true */}

      <ClassesContainer>
        <TitleContainer>
          <Title>{TITLE}</Title>
          <TierLabel labelText="TIER 1" />
        </TitleContainer>
        {classes && tier1Classes.length > 0 && (
          <TilesContainer
            className={TILES_CONTAINER_CLASS_NAME}
            key={internalTitle}
          >
            {tier1Classes.map((classItem, index) => {
              const {
                contentfulID,
                instance,
                scoID,
                secondary_user_ids: secondaryUserIds,
                title,
                user_id: ownerUserId,
              } = classItem
              const { displayTitle } =
                k8V2ContentfulMappings?.find(course => {
                  return contentfulID === course.id
                }) || {}
              const totalLessons = getClassTotalLessons(
                classItem,
                courseManager,
                program,
              )
              const completeLessons = getClassCompleteLessons(classItem)
              return (
                <ClassCard
                  dataTestId={`class-card-${toUrlSafe(title)}`}
                  displayTitle={title}
                  goalPercent={lessonCompletionGoalValue}
                  gradeDisplayTitle={displayTitle}
                  includeProgressBar={true}
                  instance={instance}
                  key={`class-card-${title}-${index}`}
                  onBlur={onClassBlur}
                  onClick={onComponentClick}
                  onFocus={() => onClassFocus(index)}
                  ownerUserId={ownerUserId}
                  program={currentProgramName}
                  ref={classMenuButtonRefs.current[index]}
                  scoID={scoID}
                  secondaryUserIds={secondaryUserIds}
                  setFocus={() => setFocus(classMenuButtonRefs.current[index])}
                  totalAmount={totalLessons}
                  totalComplete={completeLessons}
                />
              )
            })}
          </TilesContainer>
        )}
        <ButtonsContainer>
          <div style={{ position: 'relative' }}>
            <StyledButton onClick={handleCreateClassClick}>
              <StyledCreateClassIcon />
              {CREATE_CLASS_BUTTON_TEXT}
            </StyledButton>
            <HiddenCreateCourseContainer ref={createCourseRef}>
              <CreateCourse
                contentfulCourses={contentfulCourses}
                emptyClasses={true}
              />
            </HiddenCreateCourseContainer>
          </div>
          {onTrainingClick && (
            <StyledButton onClick={onTrainingClick}>
              <StyledAwardIcon />
              {TRAINING_BUTTON_TEXT}
            </StyledButton>
          )}
        </ButtonsContainer>
        <TitleContainer>
          <Title>{TIER_2_TITLE}</Title>
          <TierLabel labelText="TIER 2" />
        </TitleContainer>
        {classes && tier2Classes.length > 0 && (
          <TilesContainer
            className={`${TILES_CONTAINER_CLASS_NAME}-tier-2`}
            key={`${internalTitle}-tier-2`}
          >
            {tier2Classes.map((classItem, index) => {
              const {
                contentfulID,
                instance,
                secondary_user_ids: secondaryUserIds,
                title,
                user_id: ownerUserId,
              } = classItem
              const { displayTitle, defaultUnit } =
                k8V2ContentfulMappings?.find(course => {
                  return contentfulID === course.id
                }) || {}
              const completeLessons = getClassCompleteLessons(classItem)
              return (
                <ClassCard
                  dataTestId={`class-card-${toUrlSafe(title)}`}
                  defaultUnit={defaultUnit}
                  displayTitle={title}
                  gradeDisplayTitle={displayTitle}
                  instance={instance}
                  key={`class-card-${title}-${index}`}
                  onClick={onComponentClick}
                  ownerUserId={ownerUserId}
                  program={currentProgramName}
                  secondaryUserIds={secondaryUserIds}
                  tier={2}
                  totalComplete={completeLessons}
                />
              )
            })}
          </TilesContainer>
        )}
        <ButtonsContainer>
          <StyledButton onClick={handleCreateSmallGroupClick}>
            <StyledCreateClassIcon />
            {'Create a Small Group'}
          </StyledButton>
          <HiddenCreateCourseContainer ref={createSmallGroupRef}>
            <CreateSmallGroup
              contentfulCourses={contentfulCourses}
              emptyClasses={true}
            />
          </HiddenCreateCourseContainer>
          {onTier2ResourcesClick && (
            <StyledButton onClick={onTier2ResourcesClick}>
              <StyledAwardIcon />
              {tier2ResourcesLabel}
            </StyledButton>
          )}
        </ButtonsContainer>
      </ClassesContainer>
    </TilesContainerWrapper>
  )
}

K8DigitalProgramCardV2.propTypes = {
  classMenuButtonRefs: PropTypes.object,
  classes: PropTypes.array,
  contentfulCourses: PropTypes.array,
  courseManager: PropTypes.object,
  createCourseRef: PropTypes.object,
  createSmallGroupRef: PropTypes.object,
  currentProgramName: PropTypes.string,
  displayTitle: PropTypes.string,
  handleCreateClassClick: PropTypes.func,
  handleCreateSmallGroupClick: PropTypes.func,
  headerDataTestId: PropTypes.string,
  index: PropTypes.number,
  internalTitle: PropTypes.string,
  isK5Product: PropTypes.bool,
  isMsProduct: PropTypes.bool,
  lessonCompletionGoalValue: PropTypes.number,
  onClassBlur: PropTypes.func,
  onClassFocus: PropTypes.func,
  onComponentClick: PropTypes.func,
  onTier2ResourcesClick: PropTypes.func,
  onTrainingClick: PropTypes.func,
  programSubheader: PropTypes.string,
  setFocus: PropTypes.func,
  showFirstTimeClasses: PropTypes.bool,
  staticLinksAvailable: PropTypes.array,
  tier2ResourcesLabel: PropTypes.string,
}

export default K8DigitalProgramCardV2
